import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';

import { BagrutSubject } from '@jct/api';
import { StateFactory } from '@jct/ui';

export interface SubjectRow {
  emptyRow: boolean;
  subject?: string;
  selectedSubject?: BagrutSubject;
  units?: number;
  grade?: number;
  bonus?: number;
  finalGrade?: number;
}

const DISABLED_EVENTS = { emitEvent: false };

@Component({
  selector: 'bagrut-table',
  templateUrl: './bagrut-table.html',
  exportAs: 'bagrutTable',
})
export class BagrutTable implements OnInit {
  private _allSubjects: BagrutSubject[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private sf: StateFactory)
  { }

  @Input()
  form: UntypedFormGroup;

  @Input()
  subjects: BagrutSubject[];

  dataSource = new BehaviorSubject<AbstractControl[]>([]);
  displayColumns: string[] = ['subject', 'units', 'grade', 'bonus', 'final-grade', 'actions'];
  loadState = this.sf.create();

  get rows(): UntypedFormArray {
    return <UntypedFormArray>this.form.get('subjects');
  }

  async ngOnInit() {
    await this.loadState.inProcess();

    this._allSubjects = this.subjects;
    this.addEmptyRow();
    this._syncTable();

    await this.loadState.completed(150);
  }

  get totalUnits() {
    return this.rows.controls.length > 0 ? this.rows.controls
      .filter(x => x.value.units)
      .map(x => x.value.units)
      .reduce((total, num) => total + num, 0) : 0;
  }

  get average() {
    return this.totalUnits > 0 ? this.rows.controls
      .filter(x => x.value.units && x.value.grade)
      .map(x => x.value.units * x.value.grade)
      .reduce((total, num) => total + num, 0) / this.totalUnits : 0;
  }

  get weightedAverage() {
    return this.totalUnits > 0 ? this.rows.controls
      .filter(x => x.value.units && x.value.finalGrade)
      .map(x => x.value.units * x.value.finalGrade)
      .reduce((total, num) => total + num, 0) / this.totalUnits : 0;
  }

  removeRow(row: AbstractControl) {
    const index = this.rows.controls.indexOf(row);

    if (index < 0 || !this.canRemoveRow(row as UntypedFormGroup)) {
      return;
    }

    this.rows.removeAt(index);
    this.addEmptyRow();
    this._syncTable();
  }

  canRemoveRow(row: AbstractControl) {
    // Always keep at least one empty template row; never delete it.
    if (row.value?.emptyRow) {
      return false;
    }

    // If this is the only row in the table, it must stay.
    if (this.rows.length <= 1) {
      return false;
    }

    return true;
  }

  blockNonIntegerKey(event: KeyboardEvent) {
    if (['.', ',', 'e', 'E', '+', '-'].indexOf(event.key) >= 0) {
      event.preventDefault();
    }
  }

  private _syncTable() {
    this.dataSource.next(this.rows.controls.slice());
  }

  sanitizeInteger(row: AbstractControl, controlName: string) {
    const control = row.get(controlName);
    const value = control?.value;

    if (value == null || value === '') {
      return;
    }

    const asNumber = Number(value);

    if (!Number.isFinite(asNumber) || Number.isInteger(asNumber)) {
      return;
    }

    control.setValue(Math.trunc(asNumber), DISABLED_EVENTS);
  }

  filterSubjects(row: SubjectRow) {
    let takenSubjects = this.rows.controls
      .filter(x => x.value.selectedSubject)
      .map(x => x.value.selectedSubject.id);

    if (row.selectedSubject) {
      let index = takenSubjects.indexOf(row.selectedSubject.id);

      if (index >= 0) {
        takenSubjects.splice(index, 1);
      }
    }

    let availableSubjects = this._allSubjects
      .sort((a, b) => a.subjectName.localeCompare(b.subjectName))
      .filter(x => takenSubjects.indexOf(x.id) < 0);

    if (row.subject) {
      return availableSubjects
        .filter(x => x.subjectName.indexOf(row.subject) >= 0);
    }

    return availableSubjects;
  }

  private get hasEmptyRow() {
    return this.rows.controls.some(x => x.value.emptyRow);
  }

  private disableRow(row: UntypedFormGroup) {
    row.controls['units'].disable(DISABLED_EVENTS);
    row.controls['grade'].disable(DISABLED_EVENTS);
  }

  private addEmptyRow() {
    if (!this.hasEmptyRow) {
      const EMPTY_ROW = {
        'emptyRow': true,
        'subject': null,
        'selectedSubject': null,
        'units': null,
        'grade': null,
        'bonus': null,
        'finalGrade': null,
      };

      let row = this.fb.group(EMPTY_ROW);

      this.disableRow(row);
      this.rows.push(row);
      this._syncTable();

      row.controls['subject'].valueChanges.subscribe((value: string) => {
        if (value && value.trim().length > 0) {
          let item = this._allSubjects.find(x => x.subjectName == value);

          if (item) {
            row.patchValue({
              selectedSubject: item,
              emptyRow: false,
            }, DISABLED_EVENTS);

            row.enable(DISABLED_EVENTS);
            this.addEmptyRow();

            return;
          }
          else {
            row.patchValue({
              selectedSubject: null,
              bonus: null,
              finalGrade: null,
            }, DISABLED_EVENTS);
          }
        }
        else {
          row.patchValue({
            selectedSubject: null,
            emptyRow: true,
            units: null,
            grade: null,
            bonus: null,
            finalGrade: null,
          }, DISABLED_EVENTS);
        }

        this.disableRow(row);
        this.removeLastEmptyRow();
        this.addEmptyRow();
      });

      row.valueChanges.subscribe((changes: SubjectRow) => {
        if (!changes.emptyRow) {
          if (changes.units > 0 && changes.grade > 0) {
            let units = changes.units;
            let bonus = 0;

            if (changes.grade >= changes.selectedSubject.bonusMinGrade) {
              bonus = units >= 5 ? changes.selectedSubject.bonus5Units :
              units >= 4 ? changes.selectedSubject.bonus4Units : 0;
            }

            row.patchValue({
              bonus, finalGrade:
              bonus + changes.grade,
            }, DISABLED_EVENTS);
          }
        }
      });
    }
  }

  private removeLastEmptyRow() {
    let emptyRows = this.rows.controls.filter(x => x.value.emptyRow);

    if (emptyRows.length > 1) {
      let last = emptyRows[emptyRows.length - 1];
      let index = this.rows.controls.indexOf(last);

      this.rows.removeAt(index);
      this._syncTable();
    }
  }
}
